import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { Sidebar, Button, Modal, Spinner } from "flowbite-react";
import { HiXMark } from "react-icons/hi2";
import { FiMenu, FiArrowLeft } from "react-icons/fi";
import { API_BASE_URL } from "../../config";
import Cuenta from "./views/Cuenta";
import Ofertas from "./views/Ofertas";
import MisCompras from "./views/MisCompras";
import MisPuntos from "./views/MisPuntos";
import MenuDigital from "./Clientes";

const UserPanel = () => {
  const { usuario_id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Estados
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [cliente_id, setClienteId] = useState(null);
  const [error, setError] = useState("");
  const [usuarioAutenticado, setUsuarioAutenticado] = useState(false);
  const [configuraciones, setConfiguraciones] = useState(null);

  // ✅ Cargar usuario autenticado y cliente_id al montar el componente
useEffect(() => {
    console.log("🛠 Ejecutando useEffect en UserPanel...");

    const token = localStorage.getItem("token");
    const storedUsuarioId = localStorage.getItem("usuario_id");
    const urlClienteId = searchParams.get("menu");
    const storedClienteId = localStorage.getItem("cliente_id");

    setUsuarioAutenticado(!!(token && storedUsuarioId));

    const newClienteId = urlClienteId || storedClienteId;

    // 🚀 Evitar actualización innecesaria y loop infinito
    if (newClienteId && newClienteId !== cliente_id) {
        console.log("🔄 Cliente ID actualizado en UserPanel:", newClienteId);
        setClienteId(newClienteId);

        // ✅ Solo actualizar `localStorage` si el valor realmente cambió
        if (localStorage.getItem("cliente_id") !== newClienteId) {
            console.log("📝 Guardando cliente_id en localStorage:", newClienteId);
            localStorage.setItem("cliente_id", newClienteId);
        }
    }
}, [searchParams]); // ❌ Eliminamos `cliente_id` de las dependencias para evitar loops

  // ✅ Cargar configuraciones (Logo, etc.)
  useEffect(() => {
    if (!cliente_id) return;

    fetch(`${API_BASE_URL}/clientes/${cliente_id}/configuraciones`)
      .then((res) => res.json())
      .then((data) => {
        console.log("✅ Configuraciones cargadas:", data);
        setConfiguraciones(data);
      })
      .catch((err) => {
        console.error("❌ Error al cargar configuraciones:", err);
      });
  }, [cliente_id]);

  return (
    <div className="flex h-screen">
      {/* ✅ Header con FiMenu */}
      <header className="fixed top-0 left-0 w-full z-50 bg-opacity-80 bg-gray-900 flex items-center justify-between px-4 py-2 shadow-lg">
        {/* 🔹 Botón de menú solo si el usuario está autenticado */}
        {usuarioAutenticado && (
          <button
            onClick={() => setSidebarOpen(true)}
            className="text-white text-2xl md:hidden focus:outline-none"
          >
            <FiMenu />
          </button>
        )}

        {/* 🔹 Logo del Cliente */}
        <img
          src={
            configuraciones?.logo && configuraciones.logo.startsWith("http")
              ? `${configuraciones.logo}?v=${new Date().getTime()}`
              : `${API_BASE_URL}/${configuraciones?.logo || "static/images/default-logo.png"}`
          }
          alt="Logo"
          className="w-16 h-auto object-contain rounded-md shadow-sm"
        />

        {/* 🔹 Mostrar título solo si el usuario NO está autenticado */}
        {!usuarioAutenticado && (
          <h1 className="text-white text-lg font-bold tracking-wide">Menú Digital</h1>
        )}
      </header>

      {/* ✅ Sidebar (Menú Lateral) */}
      {usuarioAutenticado && (
        <>
          {isSidebarOpen && (
            <div
              className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
              onClick={() => setSidebarOpen(false)}
            ></div>
          )}

          <div
            className={`fixed top-0 left-0 h-full w-64 bg-white shadow-lg z-50 transition-transform ${
              isSidebarOpen ? "translate-x-0" : "-translate-x-full"
            } md:translate-x-0 md:relative`}
          >
            <button onClick={() => setSidebarOpen(false)} className="absolute top-2 right-2 text-gray-600 hover:text-gray-900">
              ✖
            </button>

            {/* ✅ Menú de Navegación */}
            <nav className="p-4">
              <ul>
                <li>
                  <Link to={`/usuario/${usuario_id}/cuenta`} className="block p-2 hover:bg-gray-200">
                    Cuenta
                  </Link>
                </li>
                <li>
                  <Link to={`/usuario/${usuario_id}/ofertas?menu=${cliente_id}`} className="block p-2 hover:bg-gray-200">
                    Ofertas y Cupones
                  </Link>
                </li>
                <li>
                  <Link to="/usuario/restaurantes" className="block p-2 hover:bg-gray-200">
                    Restaurantes
                  </Link>
                </li>
                <li>
                  <Link to="/usuario/mis-compras" className="block p-2 hover:bg-gray-200">
                    Mis Compras
                  </Link>
                </li>
                <li>
                  <Link to="/usuario/mis-puntos" className="block p-2 hover:bg-gray-200">
                    Mis Puntos
                  </Link>
                </li>
              </ul>
            </nav>

            {cliente_id && (
              <div className="p-4">
                <Button gradientDuoTone="purpleToPink" onClick={() => navigate(`/menu/${cliente_id}`)}>
                  <FiArrowLeft className="mr-2" /> Volver al Menú
                </Button>
              </div>
            )}
          </div>
        </>
      )}

      {/* ✅ Contenido Principal */}
      <div className="flex-1 p-6">
        <h1 className="text-2xl font-bold text-gray-800">
          Panel de Usuario {cliente_id ? ` - Restaurante ID: ${cliente_id}` : ""}
        </h1>

        {error && <p className="text-red-500">{error}</p>}

        {usuarioAutenticado ? (
          cliente_id ? <MenuDigital cliente_id={cliente_id} /> : <p className="text-red-500">❌ Error: No se encontró cliente_id.</p>
        ) : (
          <h2 className="text-xl font-semibold text-gray-700">Cargando menú...</h2>
        )}
      </div>
    </div>
  );
};

export default UserPanel;
